import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  BuildingOfficeIcon, 
  UserIcon,
  CalendarIcon,
  MapPinIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ArrowLeftIcon,
  EyeIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { Users, Clock, Trash2, Plus, UserCog } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../components/ui/use-toast';

import { api } from '../../services/api';
import StaffDetailsRows from '../../components/organizations/StaffDetailsRows';
import { emptyStaffRow, toFormStaffRow, toApiStaffRows } from '../../lib/staffDetails';

const EditOrganizationPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [teamAssignments, setTeamAssignments] = useState([]);
  const [staffDetails, setStaffDetails] = useState([]);

  // Institution autocomplete state
  const [institutionInput, setInstitutionInput] = useState('');
  const [institutionSuggestions, setInstitutionSuggestions] = useState([]);
  const [showInstitutionDropdown, setShowInstitutionDropdown] = useState(false);
  const [institutionLoading, setInstitutionLoading] = useState(false);

  useEffect(() => {
    if (institutionInput.length < 3) {
      setInstitutionSuggestions([]);
      setShowInstitutionDropdown(false);
      return;
    }
    let cancelled = false;
    setInstitutionLoading(true);
    api.get('/institutions', { params: { search: institutionInput } })
      .then(res => {
        if (!cancelled) {
          const list = res.data?.data || res.data || [];
          setInstitutionSuggestions(Array.isArray(list) ? list : []);
          setShowInstitutionDropdown(true);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setInstitutionLoading(false); });
    return () => { cancelled = true; };
  }, [institutionInput]);

  const addStaffRow = () => {
    setStaffDetails(prev => [...prev, emptyStaffRow()]);
  };

  const removeStaffRow = (index) => {
    setStaffDetails(prev => prev.filter((_, i) => i !== index));
  };

  const updateStaffRow = (index, field, value) => {
    setStaffDetails(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm();

  const ambulanceCount = parseInt(watch('ambulance_count') || 0);

  // Fetch organization details
  const { data: organizationData, isLoading: fetchLoading } = useQuery({
    queryKey: ['organization', id],
    queryFn: () => api.get(`/organizations/${id}`).then(res => res.data),
    enabled: !!id
  });

  // Fetch organization types
  const { data: typesData } = useQuery({
    queryKey: ['organization-types'],
    queryFn: () => api.get('/organizations/lookup/types').then(res => res.data),
  });

  // Fetch available teams
  const { data: teamsResponse } = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.get('/teams').then(res => res.data),
  });

  const organizationTypes = typesData?.data || [];
  const organization = organizationData?.data;
  const availableTeams = teamsResponse?.data || [];

  // Form'u organizasyon verileriyle doldur
  useEffect(() => {
    if (organization) {
        reset({
        organization_name: organization.organization_name || '',
        organization_type: organization.organization_type || '',
        doctor_status: organization.doctor_status || '',
        authorized_person: organization.authorized_person || '',
        authorized_phone: organization.authorized_phone || '',
        authorized_description: organization.authorized_description || '',
        start_date: organization.start_date || '',
        start_time: organization.start_time || '',
        end_date: organization.end_date || '',
        end_time: organization.end_time || '',
        amount: organization.amount || '',
        doctor_price: organization.doctor_price || '',
        protocol_112_no: organization.protocol_112_no || '',
        ambulance_count: organization.ambulance_count || 0,
        vehicle_number: organization.vehicle_number || '',
        is_multiple_organization: organization.is_multiple_organization || false,
        province: organization.province || '',
        district: organization.district || '',
        neighborhood: organization.neighborhood || '',
        street: organization.street || '',
        address: organization.address || '',
        address_description: organization.address_description || '',
        institution: organization.institution || '',
        contract_info: organization.contract_info || '',
        general_description: organization.general_description || ''
      });

      // Sync institution autocomplete input
      setInstitutionInput(organization.institution || '');

      // Load existing staff details
      if (organization.staff_details && Array.isArray(organization.staff_details)) {
        // Eski "AD SOYAD/GÖREV" kayıtları ayrı sütunlara bölünerek yüklenir
        setStaffDetails(organization.staff_details.map(toFormStaffRow));
      }

      // Load existing team assignments
      if (organization.team_assignments && organization.team_assignments.length > 0) {
        const existingAssignments = organization.team_assignments.map(ta => ({
          team_id: String(ta.team_id || ''),
          assignment_date: ta.assignment_date || '',
          assignment_time: ta.assignment_time || '',
          end_time: ta.end_time || ta.staff_names?.end_time || '',
          vehicle_plate: ta.vehicle_plate || '',
          notes: ta.notes || ''
        }));
        setTeamAssignments(existingAssignments);
      } else {
        const count = organization.ambulance_count || 0;
        const newAssignments = [];
        for (let i = 0; i < count; i++) {
          newAssignments.push({
            team_id: '',
            assignment_date: organization.start_date || '',
            assignment_time: organization.start_time || '',
            end_time: organization.end_time || '',
            vehicle_plate: '',
            notes: ''
          });
        }
        setTeamAssignments(newAssignments);
      }

      setIsLoading(false);
    }
  }, [organization, reset]);

  // Update team assignments when ambulance count changes (after initial load)
  useEffect(() => {
    if (isLoading) return;
    const count = parseInt(ambulanceCount) || 0;
    setTeamAssignments(prev => {
      if (count > prev.length) {
        const newAssignments = [...prev];
        for (let i = prev.length; i < count; i++) {
          newAssignments.push({
            team_id: '',
            assignment_date: watch('start_date') || '',
            assignment_time: watch('start_time') || '',
            end_time: watch('end_time') || '',
            vehicle_plate: '',
            notes: ''
          });
        }
        return newAssignments;
      } else if (count < prev.length) {
        return prev.slice(0, count);
      }
      return prev;
    });
  }, [ambulanceCount, isLoading]);

  const updateTeamAssignment = (index, field, value) => {
    setTeamAssignments(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Update organization mutation
  const updateOrganizationMutation = useMutation({
    mutationFn: (data) => api.put(`/organizations/${id}`, data),
    onSuccess: () => {
      toast({
        title: "Başarılı",
        description: "Organizasyon başarıyla güncellendi",
      });
      queryClient.invalidateQueries(['organizations']);
      queryClient.invalidateQueries(['organization', id]);
      navigate('/organizations');
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "Organizasyon güncellenemedi",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data) => {
    const cleanedData = Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key, 
        value === '' ? null : value
      ])
    );

    cleanedData.team_assignments = teamAssignments.filter(ta => ta.team_id);
    cleanedData.staff_details = toApiStaffRows(staffDetails);
    updateOrganizationMutation.mutate(cleanedData);
  };

  if (fetchLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Organizasyon Bulunamadı</h2>
        <Button onClick={() => navigate('/organizations')}>
          Organizasyonlara Dön
        </Button>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      'planned': { color: 'bg-blue-100 text-blue-800', text: 'Planlanan' },
      'active': { color: 'bg-green-100 text-green-800', text: 'Aktif' },
      'completed': { color: 'bg-gray-100 text-gray-800', text: 'Tamamlanan' },
      'cancelled': { color: 'bg-red-100 text-red-800', text: 'İptal' }
    };
    const config = statusConfig[status] || statusConfig['planned'];
    return <Badge className={config.color}>{config.text}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/organizations')}
            className="flex items-center space-x-2"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <span>Geri</span>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Organizasyon Düzenle</h1>
            <p className="text-gray-600">
              {organization.organization_name || 'İsimsiz Organizasyon'}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {getStatusBadge(organization.status)}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/organizations/${id}`)}
            className="flex items-center space-x-2"
          >
            <EyeIcon className="h-4 w-4" />
            <span>Detay Görüntüle</span>
          </Button>
        </div>
      </div>

      {/* Organizasyon Bilgi Kartı */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BuildingOfficeIcon className="h-5 w-5" />
            <span>Organizasyon Bilgileri</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium">Numara:</span>
              <span className="ml-2">{organization.organization_number || 'Belirtilmemiş'}</span>
            </div>
            <div>
              <span className="font-medium">Oluşturan:</span>
              <span className="ml-2">{organization.created_by_name || 'Sistem'}</span>
            </div>
            <div>
              <span className="font-medium">Oluşturma Tarihi:</span>
              <span className="ml-2">{organization.created_at ? new Date(organization.created_at).toLocaleDateString('tr-TR') : 'Belirtilmemiş'}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Düzenleme Formu */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sol Panel */}
          <div className="space-y-6">
            {/* Temel Bilgiler */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center space-x-2">
                  <BuildingOfficeIcon className="h-5 w-5" />
                  <span>Temel Bilgiler</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="organization_name">Organizasyon Adı *</Label>
                  <Input
                    id="organization_name"
                    {...register('organization_name', { required: 'Organizasyon adı gerekli' })}
                    className={errors.organization_name ? 'border-destructive' : ''}
                  />
                  {errors.organization_name && (
                    <p className="text-sm text-destructive">{errors.organization_name.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="organization_type">Organizasyon Türü</Label>
                    <select
                      id="organization_type"
                      {...register('organization_type')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="">Tür seçin</option>
                      {organizationTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="doctor_status">Doktor Durumu</Label>
                    <select
                      id="doctor_status"
                      {...register('doctor_status')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="">Seçin</option>
                      <option value="Gerekli">Gerekli</option>
                      <option value="Gerekli Değil">Gerekli Değil</option>
                      <option value="Standby">Standby</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Yetkili Bilgileri */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center space-x-2">
                  <UserIcon className="h-5 w-5" />
                  <span>Yetkili Kişi</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="authorized_person">Yetkili Kişi</Label>
                    <Input
                      id="authorized_person"
                      {...register('authorized_person')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="authorized_phone">Yetkili Telefon</Label>
                    <Input
                      id="authorized_phone"
                      {...register('authorized_phone')}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="authorized_description">Yetkili Açıklama</Label>
                  <Textarea
                    id="authorized_description"
                    {...register('authorized_description')}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Tarih ve Saat */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center space-x-2">
                  <CalendarIcon className="h-5 w-5" />
                  <span>Tarih ve Saat</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Başlangıç Tarihi</Label>
                    <Input
                      type="date"
                      id="start_date"
                      {...register('start_date')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="start_time">Başlangıç Saati</Label>
                    <Input
                      type="time"
                      id="start_time"
                      {...register('start_time')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="end_date">Bitiş Tarihi</Label>
                    <Input
                      type="date"
                      id="end_date"
                      {...register('end_date')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="end_time">Bitiş Saati</Label>
                    <Input
                      type="time"
                      id="end_time"
                      {...register('end_time')}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sağ Panel */}
          <div className="space-y-6">
            {/* Mali Detaylar */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center space-x-2">
                  <CurrencyDollarIcon className="h-5 w-5" />
                  <span>Mali Detaylar</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="amount">Tutar (TL)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      id="amount"
                      {...register('amount')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="doctor_price">Doktor Ücreti (TL)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      id="doctor_price"
                      {...register('doctor_price')}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="protocol_112_no">112 P. No</Label>
                    <Input
                      id="protocol_112_no"
                      {...register('protocol_112_no')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ambulance_count">Ambulans Sayısı</Label>
                    <Input
                      type="number"
                      min="0"
                      id="ambulance_count"
                      {...register('ambulance_count')}
                    />
                  </div>

                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="vehicle_number">Araç No</Label>
                    <Input
                      id="vehicle_number"
                      {...register('vehicle_number')}
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_multiple_organization"
                    {...register('is_multiple_organization')}
                    className="h-4 w-4 text-primary border-gray-300 rounded"
                  />
                  <Label htmlFor="is_multiple_organization">Çoklu Organizasyon</Label>
                </div>
              </CardContent>
            </Card>

            {/* Adres Bilgileri */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center space-x-2">
                  <MapPinIcon className="h-5 w-5" />
                  <span>Adres</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="province">İl</Label>
                    <Input
                      id="province"
                      {...register('province')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="district">İlçe</Label>
                    <Input
                      id="district"
                      {...register('district')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="neighborhood">Mahalle</Label>
                    <Input
                      id="neighborhood"
                      {...register('neighborhood')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="street">Cadde/Sokak</Label>
                    <Input
                      id="street"
                      {...register('street')}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Adres</Label>
                  <Textarea
                    id="address"
                    {...register('address')}
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address_description">Adres Tarifi</Label>
                  <Textarea
                    id="address_description"
                    {...register('address_description')}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Ek Bilgiler */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center space-x-2">
                  <DocumentTextIcon className="h-5 w-5" />
                  <span>Ek Bilgiler</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="institution">Kurum</Label>
                  <div className="relative">
                    <Input
                      id="institution"
                      value={institutionInput}
                      onChange={e => {
                        setInstitutionInput(e.target.value);
                        setValue('institution', e.target.value);
                      }}
                      onBlur={() => setTimeout(() => setShowInstitutionDropdown(false), 150)}
                      placeholder="3 karakter yazın, kurumlar listelenecek"
                      autoComplete="off"
                    />
                    {institutionLoading && (
                      <div className="absolute right-2 top-2.5">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />
                      </div>
                    )}
                    {showInstitutionDropdown && institutionSuggestions.length > 0 && (
                      <ul className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-52 overflow-y-auto text-sm">
                        {institutionSuggestions.map(inst => (
                          <li
                            key={inst.id}
                            className="px-3 py-2 cursor-pointer hover:bg-blue-50 hover:text-blue-700"
                            onMouseDown={() => {
                              setInstitutionInput(inst.name);
                              setValue('institution', inst.name);
                              setShowInstitutionDropdown(false);
                            }}
                          >
                            <span className="font-medium">{inst.name}</span>
                            {inst.code && <span className="ml-2 text-gray-400 text-xs">{inst.code}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                    {showInstitutionDropdown && !institutionLoading && institutionSuggestions.length === 0 && institutionInput.length >= 3 && (
                      <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-sm px-3 py-2 text-sm text-gray-400">
                        Sonuç bulunamadı
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contract_info">Sözleşme</Label>
                  <Input
                    id="contract_info"
                    {...register('contract_info')}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="general_description">Açıklama</Label>
                  <Textarea
                    id="general_description"
                    {...register('general_description')}
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Team Assignments - Full Width */}
        {ambulanceCount > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center space-x-2">
                <UserGroupIcon className="h-5 w-5" />
                <span>Ekip Atamaları</span>
              </CardTitle>
              <CardDescription>
                {ambulanceCount} ambulans için ekip atamalarını yapın
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {teamAssignments.map((assignment, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50/50">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
                        <Users className="h-4 w-4" />
                        <span>Ambulans {index + 1}</span>
                      </h4>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Ekip Seç</Label>
                        <select
                          value={assignment.team_id}
                          onChange={(e) => updateTeamAssignment(index, 'team_id', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        >
                          <option value="">Ekip seçin</option>
                          {availableTeams.map((team) => (
                            <option
                              key={team.id}
                              value={team.id}
                              disabled={teamAssignments.some((ta, i) => i !== index && ta.team_id === String(team.id))}
                            >
                              {team.code} - {team.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs flex items-center space-x-1">
                            <Clock className="h-3 w-3" />
                            <span>Başlangıç Saati</span>
                          </Label>
                          <Input
                            type="time"
                            value={assignment.assignment_time}
                            onChange={(e) => updateTeamAssignment(index, 'assignment_time', e.target.value)}
                            className="h-9 text-sm"
                          />
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs flex items-center space-x-1">
                            <Clock className="h-3 w-3" />
                            <span>Bitiş Saati</span>
                          </Label>
                          <Input
                            type="time"
                            value={assignment.end_time}
                            onChange={(e) => updateTeamAssignment(index, 'end_time', e.target.value)}
                            className="h-9 text-sm"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Araç Plakası</Label>
                          <Input
                            value={assignment.vehicle_plate}
                            onChange={(e) => updateTeamAssignment(index, 'vehicle_plate', e.target.value)}
                            placeholder="Araç plakası"
                            className="h-9 text-sm"
                          />
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs">Not</Label>
                          <Input
                            value={assignment.notes}
                            onChange={(e) => updateTeamAssignment(index, 'notes', e.target.value)}
                            placeholder="Ekip notu"
                            className="h-9 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Staff Details */}
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center space-x-2">
                <UserCog className="h-5 w-5" />
                <span>Ek Personel Detayları</span>
              </CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addStaffRow}>
                <Plus className="h-4 w-4 mr-1" />
                Satır Ekle
              </Button>
            </div>
            <CardDescription>Faaliyet raporunda görünecek ek personel bilgilerini girin</CardDescription>
          </CardHeader>
          <CardContent>
            <StaffDetailsRows
              rows={staffDetails}
              onChange={updateStaffRow}
              onRemove={removeStaffRow}
            />
          </CardContent>
        </Card>

        {/* Sayfa Alt Butonları */}
        <div className="flex justify-end space-x-4 mt-6 pt-6 border-t bg-white sticky bottom-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/organizations')}
          >
            İptal
          </Button>
          <Button
            type="submit"
            disabled={updateOrganizationMutation.isLoading}
          >
            {updateOrganizationMutation.isLoading ? 'Güncelleniyor...' : 'Güncelle'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditOrganizationPage;